/**
 * Bloco 13 v3 alteração — deriva `patches[]` (discriminated union) a
 * partir dos `tipos_alteracao` marcados pelo operador + valores do form
 * + vigente parser_json (devolvido por `GET /parsear-vigente-preview`).
 *
 * Premissas:
 * - D+E cobre só `substituir_campo` + `substituir_clausula`. Demais
 *   tipos (`inserir_clausula`, `remover_clausula`, `ia_narrow`) são
 *   Onda F e o engine_routing devolve 422 se vierem no payload.
 * - Diff do quadro de sócios NÃO é patch explícito — backend deriva
 *   da diferença `socios[]` payload vs vigente parser_json. Só
 *   `quadro_societario` em `tipos_alteracao` sinaliza intenção.
 * - `clausula_numero` vem do `numero` da `ClausulaEstruturada` —
 *   localizada por `tipo_semantico` no vigente parseado.
 *
 * Quando a derivação não cobre (ex: operador marcou `administracao` ou
 * `outras`), `cobertoEmDE === false` → nao ha' o que aplicar: o caller avisa
 * o operador em vez de gerar (o fallback pra v2 sumiu com o engine).
 */
import type {
  IPatchClausula,
  IPatchSubstituirCampo,
  IPatchSubstituirClausula,
  TipoAlteracao,
} from '../../../../hooks/useSocietario';
import { parsearEnderecoLivre } from './parsearEnderecoLivre';

interface IClausulaParsed {
  numero: number | null;
  tipo_semantico: string;
  [key: string]: any;
}

interface IParteParsed {
  rotulo: string;
  clausulas?: IClausulaParsed[];
}

interface IVigenteParserJson {
  partes?: IParteParsed[];
}

interface ICnaeForm {
  codigo?: string;
  descricao?: string;
  principal?: boolean;
}

interface IEnderecoEstruturadoForm {
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

/** Subset do IWizardFormValues necessário pra derivar os patches. */
export interface IValoresPatchesV3 {
  razao_social?: string | null;
  nome_fantasia?: string | null;
  capital_social?: number | null;
  /** Total de quotas após alteração — usado no patch dict do capital. */
  capital_qtd_quotas?: number | null;
  /** Valor unitário da quota após alteração — usado no patch dict do capital. */
  capital_valor_quota?: number | null;
  endereco?: string | null;
  /**
   * Estruturado: a sede da constituição e, desde 08/10, a sede NOVA da alteração.
   * Vazio (rascunho antigo de alteração), derivarPatchesV3 chama
   * `parsearEnderecoLivre` no texto de `endereco`.
   */
  endereco_estruturado?: IEnderecoEstruturadoForm | null;
  objeto_social?: string | null;
  cnaes?: ICnaeForm[];
  /** Filiais abertas pelo ato (tipo `abertura_filial`): o endereço e os dados facultativos. */
  filiais?: Array<IEnderecoEstruturadoForm & IDadosDaFilialForm> | null;
  /** Filiais encerradas pelo ato (tipo `extincao_filial`): endereço, NIRE e CNPJ. */
  filiais_fechar?: Array<
    IEnderecoEstruturadoForm & { nire?: string | null; cnpj?: string | null }
  > | null;
  /**
   * Filiais alteradas (tipo `alteracao_filial`): CNPJ, NIRE e endereço de hoje; muda o endereço
   * (`novo`, salvo `muda_endereco: false`), as atividades, o capital destacado ou o nome fantasia.
   */
  filiais_alterar?: Array<
    IEnderecoEstruturadoForm &
      IDadosDaFilialForm & {
        nire?: string | null;
        cnpj?: string | null;
        novo?: IEnderecoEstruturadoForm | null;
        muda_endereco?: boolean | null;
      }
  > | null;
  /**
   * Valores ATUAIS do vigente (snapshot da extração). Novo igual ao atual não é
   * alteração: marcar o tipo e não editar mandava patch com o mesmo valor, e trocar
   * só os CNAEs gravava o objeto antigo como o novo (varredura de 25/09).
   */
  atuais?: IValoresAtuais | null;
}

export interface IValoresAtuais {
  razao_social?: string | null;
  nome_fantasia?: string | null;
  endereco?: string | null;
  objeto_social?: string | null;
  cidade_assinatura?: string | null;
  cnaes?: string[];
}

/** Os valores atuais a partir do snapshot da extração do vigente. */
export function atuaisDoVigente(dados: any): IValoresAtuais | null {
  if (!dados) return null;
  return {
    razao_social: dados.razao_social ?? null,
    nome_fantasia: dados.nome_fantasia ?? null,
    endereco: dados.endereco_atual ?? null,
    objeto_social: dados.objeto_social_atual ?? null,
    cidade_assinatura: dados.cidade_assinatura ?? null,
    cnaes: (dados.cnaes_atual ?? [])
      .map((c: any) => String(c?.codigo ?? '').trim())
      .filter(Boolean),
  };
}

/** Novo igual ao atual (ignorando acento, caixa e espaço): não é alteração. */
export function igualAoAtual(novo: string, atual?: string | null): boolean {
  return !!(atual ?? '').trim() && achatar(novo) === achatar(atual ?? '');
}

/**
 * A cidade de assinatura depois de a sede nova mudar de cidade, ou `null` para não mexer.
 * Os contratos saíam com a cidade antiga no local e data. Segue a sede enquanto o operador
 * não escolheu outra:
 * - vazia, ou igual à cidade que a sede nova tinha: continua seguindo; sede nova apagada (ou
 *   o tipo desmarcado), volta à do vigente;
 * - a sede nova ganha cidade pela primeira vez: segue se era a cidade da sede atual. Depois
 *   disso, a cidade da sede atual digitada de volta é escolha do operador e fica.
 */
export function cidadeDeAssinaturaComASede(
  assinatura: string | null | undefined,
  cidadeNova: string | null | undefined,
  cidadeAnterior?: string | null,
  sedeAtual?: string | null,
  doVigente?: string | null,
): string | null {
  const nova = (cidadeNova ?? '').trim();
  const atual = (assinatura ?? '').trim();
  const hoje = achatar(atual);
  const anterior = achatar(cidadeAnterior ?? '');
  const seguia = !hoje || (!!anterior && hoje === anterior);
  if (!nova) return seguia && hoje ? (doVigente ?? '').trim() : null;
  // Mesma cidade: só acerta a grafia de quem vinha seguindo ("goiania" → "Goiânia").
  if (hoje === achatar(nova)) return seguia && atual !== nova ? nova : null;
  if (seguia) return nova;
  const palavras = (texto: string) => {
    const limpo = achatar(texto).replace(/[^A-Z0-9]+/g, ' ');
    return ` ${limpo.trim()} `;
  };
  // Já cita a cidade nova ("Anápolis/GO"), ou a sede nova já tinha outra cidade.
  if (anterior || palavras(atual).includes(palavras(nova))) return null;
  return palavras(sedeAtual ?? '').includes(palavras(atual)) ? nova : null;
}

/** Os códigos dos CNAEs com conteúdo, em ordem, para comparar dois conjuntos. */
export function chaveDosCnaes(codigos: Array<string | null | undefined>): string {
  return codigos
    .map(c => (c ?? '').trim())
    .filter(Boolean)
    .sort()
    .join('|');
}

/**
 * Tipos de alteração que NÃO geram patch explícito em D+E:
 * - `quadro_societario`: derivado de diff socios[] payload vs vigente.
 * - `administracao`: Onda F (sem cobertura D+E).
 * - `outras`: wildcard de texto livre → cai pra v2 (IA generativa).
 */
const TIPOS_FORA_DE_DE: ReadonlySet<TipoAlteracao> = new Set<TipoAlteracao>([
  // Wave 2 (2026-06-19): 'administracao' SAIU daqui — agora roteia pro v3
  // (tratado como quadro_societario abaixo: sem patch, consolidado pelo socios[]).
  'outras',
]);

/**
 * Encontra a primeira cláusula com o `tipo_semantico` solicitado.
 *
 * **Ordem de busca**: prefere Parte B (consolidação) ou parte única
 * (constituição), depois Parte A (delta). Razão: quando o vigente é
 * uma alteração consolidada (ex: 1ª ALTERAÇÃO CONTRATUAL do HUNTER),
 * a Parte A é o **delta histórico** (cl. "O sócio resolve alterar a
 * cl. 3ª..." pode ter `tipo_semantico='objeto'` mas é texto da
 * alteração anterior, não a cláusula vigente). A nova alteração precisa
 * mexer na Parte B (estado consolidado vigente).
 *
 * Em constituições o parser só tem 1 parte (rotulo='constituicao') —
 * busca cai nela direto.
 */
function localizarClausulaPorSemantico(
  vigente: IVigenteParserJson,
  tipoSemantico: string,
): IClausulaParsed | null {
  const partes = vigente.partes ?? [];
  const buscar = (filtrar: (rotulo: string) => boolean): IClausulaParsed | null => {
    const partesFiltradas = partes.filter(p => filtrar(p.rotulo));
    for (let i = 0; i < partesFiltradas.length; i += 1) {
      const clausulas = partesFiltradas[i].clausulas ?? [];
      const match = clausulas.find(cl => cl.tipo_semantico === tipoSemantico && cl.numero != null);
      if (match) return match;
    }
    return null;
  };
  // 1ª passada: Parte B / constituicao (rótulo não-A)
  const naoA = buscar(r => r !== 'A');
  if (naoA) return naoA;
  // 2ª passada (fallback): Parte A — só pra vigentes anômalos sem Parte B
  return buscar(r => r === 'A');
}

/**
 * `tipo_semantico='denominacao'` no back agrupa razão social + sede.
 * Quando o vigente separa em cláusulas distintas (cl. 1ª NOME EMPRESARIAL,
 * cl. 2ª ENDEREÇO DA SEDE) ambas têm `denominacao` — `localizarClausulaPorSemantico`
 * pega a primeira (razão), errando se o patch é `campo='endereco'`. Esta função
 * varre todas as `denominacao` e filtra por palavras-chave no título/caput.
 */
function localizarClausulaDeSede(vigente: IVigenteParserJson): IClausulaParsed | null {
  const partes = vigente.partes ?? [];
  const palavrasSede = /sede|endere[çc]o|domic[íi]lio|filia(?:l|is)/i;
  const acharNas = (filtrar: (rotulo: string) => boolean): IClausulaParsed | null => {
    const partesFiltradas = partes.filter(p => filtrar(p.rotulo));
    for (let i = 0; i < partesFiltradas.length; i += 1) {
      const clausulas = partesFiltradas[i].clausulas ?? [];
      const match = clausulas.find(cl => {
        if (cl.tipo_semantico !== 'denominacao' || cl.numero == null) return false;
        const titulo = (cl.titulo_secao ?? '') as string;
        const caput = (cl.caput ?? '') as string;
        return palavrasSede.test(titulo) || palavrasSede.test(caput);
      });
      if (match) return match;
    }
    return null;
  };
  // Mesma estratégia da `localizarClausulaPorSemantico`: prefere Parte B
  // (consolidação) ou constituição; fallback Parte A.
  const naoA = acharNas(r => r !== 'A');
  if (naoA) return naoA;
  const emA = acharNas(r => r === 'A');
  if (emA) return emA;
  // Fallback final: primeira denominação em qualquer parte (vigente atípico
  // que mistura razão + sede em 1 cláusula).
  return localizarClausulaPorSemantico(vigente, 'denominacao');
}

export interface IDerivarPatchesResult {
  patches: IPatchClausula[];
  /**
   * `true` quando TODOS os tipos_alteracao marcados foram cobertos
   * pelos patches gerados (incluindo `quadro_societario` que não
   * gera patch mas é coberto pelo diff de sócios). `false` quando
   * algum tipo não-coberto está presente — caller deve usar v2.
   */
  cobertoEmDE: boolean;
  /** Tipos que não puderam ser convertidos em patch D+E. */
  tiposNaoCobertos: TipoAlteracao[];
  /**
   * Razões por que `cobertoEmDE` é falso (debug + UX). Ex:
   * "cláusula tipo_semantico='capital' não encontrada no vigente".
   */
  motivos: string[];
  /** O que gera mas merece conferência no documento (não bloqueia). */
  avisos: string[];
}

interface IBuilderCtx {
  patches: IPatchClausula[];
  tiposNaoCobertos: TipoAlteracao[];
  motivos: string[];
  avisos: string[];
  valores: IValoresPatchesV3;
  vigente: IVigenteParserJson;
}

function tratarRazaoSocial(ctx: IBuilderCtx): void {
  const cl = localizarClausulaPorSemantico(ctx.vigente, 'denominacao');
  if (!cl || cl.numero == null) {
    ctx.tiposNaoCobertos.push('razao_social');
    ctx.motivos.push(
      "tipo_alteracao='razao_social': cláusula com tipo_semantico='denominacao' não encontrada no vigente.",
    );
    return;
  }
  // ⚠️ Campo vazio precisa entrar em `tiposNaoCobertos`, não só sair calado.
  // Antes era `if (valor) { push }` SEM else: nenhum patch, nenhum motivo,
  // nenhum tipo não-coberto — `cobertoEmDE` seguia `true`, e bastava haver
  // qualquer outro patch (ou `alteraQuadro`) pra geração PASSAR pelo gate e o
  // documento sair SEM a alteração que o operador pediu, sem nenhum aviso.
  // Espelha `tratarNomeFantasia`, que já fazia certo.
  const razaoSocial = (ctx.valores.razao_social ?? '').toString().trim();
  if (razaoSocial.length === 0) {
    ctx.tiposNaoCobertos.push('razao_social');
    ctx.motivos.push("tipo_alteracao='razao_social': campo vazio. Preencha a nova razão social.");
    return;
  }
  if (igualAoAtual(razaoSocial, ctx.valores.atuais?.razao_social)) {
    ctx.tiposNaoCobertos.push('razao_social');
    ctx.motivos.push(
      "tipo_alteracao='razao_social': a nova razão social é igual à atual. Digite a nova ou desmarque o tipo.",
    );
    return;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: cl.numero,
    campo: 'razao_social',
    valor_novo: razaoSocial,
  };
  ctx.patches.push(patch);
}

/**
 * Nome fantasia — handler PRÓPRIO desde 2026-08-13.
 *
 * Antes vivia dentro do `tratarRazaoSocial`, o que tinha duas consequências:
 * mudar só a fantasia obrigava a marcar "Razão social" (e o patch de razão
 * social ia junto, sobrevivendo só pelo no-op do backend), e o operador não
 * tinha como declarar a fantasia como o ato que ela é.
 *
 * Aponta a MESMA cláusula que a razão social (`denominacao` agrupa as duas no
 * vigente). Quando os dois tipos estão marcados, saem dois patches pra mesma
 * cláusula — o backend soma as redações na Parte B. Isso é suportado de
 * propósito; antes o segundo patch era descartado em silêncio.
 */
function tratarNomeFantasia(ctx: IBuilderCtx): void {
  const cl = localizarClausulaPorSemantico(ctx.vigente, 'denominacao');
  if (!cl || cl.numero == null) {
    ctx.tiposNaoCobertos.push('nome_fantasia');
    ctx.motivos.push(
      "tipo_alteracao='nome_fantasia': cláusula com tipo_semantico='denominacao' não encontrada no vigente.",
    );
    return;
  }
  // String vazia é um caso LEGÍTIMO aqui: supressão de nome fantasia é um ato
  // do DREI (`alteradora_nome_fantasia_supressao`). Mas o form manda `""`
  // também quando o operador simplesmente não preencheu, e os dois são
  // indistinguíveis no payload — então vazio NÃO gera patch. Supressão passa a
  // exigir suporte explícito (`V-FANTASIA-01`), em vez de nascer de um campo
  // em branco.
  const nomeFantasia = (ctx.valores.nome_fantasia ?? '').trim();
  if (nomeFantasia.length === 0) {
    ctx.tiposNaoCobertos.push('nome_fantasia');
    ctx.motivos.push(
      "tipo_alteracao='nome_fantasia': campo vazio. Adoção/alteração exigem o novo nome; supressão ainda não é suportada pelo wizard.",
    );
    return;
  }
  if (igualAoAtual(nomeFantasia, ctx.valores.atuais?.nome_fantasia)) {
    ctx.tiposNaoCobertos.push('nome_fantasia');
    ctx.motivos.push(
      "tipo_alteracao='nome_fantasia': o novo nome fantasia é igual ao atual. Digite o novo ou desmarque o tipo.",
    );
    return;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: cl.numero,
    campo: 'nome_fantasia',
    valor_novo: nomeFantasia,
  };
  ctx.patches.push(patch);
}

/** Chave de comparação de texto: sem acento, caixa alta, espaço único. */
function achatar(texto: string): string {
  const semAcento = Array.from(texto.normalize('NFD'))
    .filter(c => {
      const cp = c.codePointAt(0) ?? 0;
      return cp < 0x300 || cp > 0x36f;
    })
    .join('');
  return Array.from(semAcento)
    .map(c => (c.trim() === '' ? ' ' : c))
    .join('')
    .split(' ')
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
}

/** O nome já está escrito na cláusula de denominação do vigente? */
function fantasiaJaEstaNoVigente(vigente: IVigenteParserJson, nome: string): boolean {
  const alvo = achatar(nome);
  const cl = localizarClausulaPorSemantico(vigente, 'denominacao');
  if (!alvo || !cl) return false;
  const texto = [cl.titulo_secao, cl.caput, cl.texto_integral_preservado]
    .filter(v => typeof v === 'string')
    .join(' ');
  return achatar(texto).includes(alvo);
}

function tratarEndereco(ctx: IBuilderCtx): void {
  // `denominacao` agrupa razão+sede no back. Vigentes que separam (HUNTER
  // tem cl. 1ª NOME e cl. 2ª SEDE) exigem heurística pra achar a cláusula
  // CERTA — `localizarClausulaDeSede` filtra denominacao por palavras-chave
  // de sede no título/caput.
  const cl = localizarClausulaDeSede(ctx.vigente);
  if (!cl || cl.numero == null) {
    ctx.tiposNaoCobertos.push('endereco');
    ctx.motivos.push(
      "tipo_alteracao='endereco': cláusula com tipo_semantico='denominacao' (sede) não encontrada no vigente.",
    );
    return;
  }
  // Backend `PatchSubstituirCampo(campo='endereco')` exige `valor_novo`
  // como dict {logradouro, numero, complemento, bairro, cidade, uf, cep}.
  // Usa o `endereco_estruturado` (o formulário da sede nova); só com ele vazio
  // (rascunho antigo) faz parse best-effort do texto de `endereco`.
  const est = ctx.valores.endereco_estruturado;
  // trim() em cada campo: "  " (só espaços) é truthy e fazia o form emitir um
  // patch de endereço com todos os campos vazios — documento com sede em
  // branco, sem aviso.
  const temEstruturado =
    est &&
    [est.logradouro, est.numero, est.bairro, est.cidade, est.uf, est.cep].some(
      v => (v ?? '').toString().trim().length > 0,
    );

  let valorNovo: Record<string, unknown>;
  if (temEstruturado) {
    valorNovo = {
      logradouro: (est?.logradouro ?? '').toString().trim(),
      numero: (est?.numero ?? '').toString().trim(),
      complemento: (est?.complemento ?? '').toString().trim(),
      bairro: (est?.bairro ?? '').toString().trim(),
      cidade: (est?.cidade ?? '').toString().trim(),
      uf: (est?.uf ?? '').toString().trim(),
      cep: (est?.cep ?? '').toString().trim(),
    };
  } else if ((ctx.valores.endereco ?? '').trim()) {
    if (igualAoAtual(ctx.valores.endereco!, ctx.valores.atuais?.endereco)) {
      ctx.tiposNaoCobertos.push('endereco');
      ctx.motivos.push(
        "tipo_alteracao='endereco': o novo endereço é igual ao atual. Digite o novo ou desmarque o tipo.",
      );
      return;
    }
    valorNovo = { ...parsearEnderecoLivre(ctx.valores.endereco!.trim()) };
  } else {
    // ⚠️ `tiposNaoCobertos` junto com o motivo — sem ele `cobertoEmDE` seguia
    // `true` e o documento saía sem a alteração de endereço, calado.
    ctx.tiposNaoCobertos.push('endereco');
    ctx.motivos.push(
      "tipo_alteracao='endereco' marcado mas valores.endereco e endereco_estruturado vazios.",
    );
    return;
  }

  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: cl.numero,
    campo: 'endereco',
    valor_novo: valorNovo,
  };
  ctx.patches.push(patch);
}

function tratarCapital(ctx: IBuilderCtx): void {
  const cl = localizarClausulaPorSemantico(ctx.vigente, 'capital');
  if (!cl || cl.numero == null) {
    ctx.tiposNaoCobertos.push('capital');
    ctx.motivos.push(
      "tipo_alteracao='capital': cláusula com tipo_semantico='capital' não encontrada no vigente.",
    );
    return;
  }
  // ⚠️ `tiposNaoCobertos` junto — sem ele `cobertoEmDE` seguia `true`. E o
  // capital é o caso mais perigoso: ele está em `TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS`,
  // então `alteraQuadro` já era `true` só por estar marcado, e a geração passava
  // pelo gate com ZERO patches — documento sem a alteração, sem aviso nenhum.
  if (ctx.valores.capital_social == null || ctx.valores.capital_social === ('' as any)) {
    ctx.tiposNaoCobertos.push('capital');
    ctx.motivos.push("tipo_alteracao='capital' marcado mas valores.capital_social vazio.");
    return;
  }
  // `<InputForm type="number">` persiste como string no RHF. Backend
  // passa direto pro filtro Jinja `moeda` que exige Decimal/float/int —
  // gera `TypeError` se vem string. Coerce aqui (já filtrado por null
  // acima; NaN também sai como motivo, não cria patch inválido).
  const capNum = Number(ctx.valores.capital_social);
  if (!Number.isFinite(capNum) || capNum <= 0) {
    ctx.tiposNaoCobertos.push('capital');
    ctx.motivos.push(
      `tipo_alteracao='capital': valor inválido (${ctx.valores.capital_social}) — esperado número > 0.`,
    );
    return;
  }
  // Backend `_gerar_capital_social` aceita `valor_novo` como dict
  // {capital_social, capital_qtd_quotas, capital_valor_quota}. Quando
  // recebe valor simples, qtd_quotas/valor_quota ficam None e o template
  // jinja `alteradora_capital_social.jinja.xml` quebra no filtro `moeda`.
  // Sempre enviamos os 3 — qtd_quotas/valor_quota inferidos quando faltam:
  //   - se qtd e valor preenchidos → usa direto
  //   - se só qtd → valor = total / qtd
  //   - se só valor → qtd = total / valor
  //   - se nenhum → valor=1 (padrão DREI), qtd = total
  let qtdNum = Number(ctx.valores.capital_qtd_quotas);
  let valorQuotaNum = Number(ctx.valores.capital_valor_quota);
  const qtdValida = Number.isFinite(qtdNum) && qtdNum > 0;
  const valorQuotaValida = Number.isFinite(valorQuotaNum) && valorQuotaNum > 0;
  if (qtdValida && !valorQuotaValida) {
    valorQuotaNum = capNum / qtdNum;
  } else if (!qtdValida && valorQuotaValida) {
    qtdNum = Math.round(capNum / valorQuotaNum);
  } else if (!qtdValida && !valorQuotaValida) {
    valorQuotaNum = 1;
    qtdNum = capNum;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: cl.numero,
    campo: 'capital_social',
    valor_novo: {
      capital_social: capNum,
      capital_qtd_quotas: qtdNum,
      capital_valor_quota: valorQuotaNum,
    },
  };
  ctx.patches.push(patch);
}

function tratarObjetoSocial(ctx: IBuilderCtx): void {
  const cl = localizarClausulaPorSemantico(ctx.vigente, 'objeto');
  if (!cl || cl.numero == null) {
    ctx.tiposNaoCobertos.push('objeto_social');
    ctx.motivos.push(
      "tipo_alteracao='objeto_social': cláusula com tipo_semantico='objeto' não encontrada no vigente.",
    );
    return;
  }
  // objeto_social mexe na cláusula inteira (texto livre + tabela de
  // CNAEs) → `substituir_clausula`. Backend renderiza
  // `jinja_clausulas/objeto_social.jinja.xml` com `novo_corpo`. Chaves
  // esperadas (substituir_clausula.py:72-76 monta o contexto como
  // `numero_clausula + titulo_secao + **novo_corpo`):
  //   - `objeto_social_texto` (string — preâmbulo do template
  //     "A sociedade tem por objeto social {X}")
  //   - `cnaes` (lista de {codigo, descricao, principal?})
  //   - `xml_extras_preservados` (auto-injetado pelo back)
  //
  // Estratégia (issue #1158): com CNAEs estruturados, o `objeto_social_texto`
  // são as DESCRIÇÕES OFICIAIS concatenadas por "; " (o `{% for cnae %}`
  // apresenta a lista de códigos abaixo). As descrições vêm do tb_cnae
  // (enriquecidas no back / buscadas no CnaeSelector), não do texto do
  // vigente. Sem duplicação: são nomes de atividade ("Produção musical"),
  // não prosa que repete o prefixo "A sociedade tem por objeto social".
  // A lista CONCLA estruturada é a fonte da verdade (não o textarea legacy).
  // Sem CNAEs estruturados, usa o textarea legacy (compat).
  const cnaesEstruturados = (ctx.valores.cnaes ?? [])
    .filter(c => c.codigo?.trim() || c.descricao?.trim())
    .map(c => ({
      codigo: c.codigo?.trim() ?? '',
      descricao: c.descricao?.trim() ?? '',
      principal: !!c.principal,
    }));
  // O texto digitado vence: vai pro contrato como está (Juliana, 21/09), inclusive colado
  // igual ao atual quando os CNAEs mudam. Trocá-lo pelas descrições dos CNAEs apagava as
  // atividades que o operador quis manter (review do PR #772); o passo Empresa avisa que
  // o texto é o atual. Sem texto, as descrições. Texto e CNAEs iguais aos atuais: nada muda.
  const digitado = (ctx.valores.objeto_social ?? '').trim();
  const textoEhOAtual = igualAoAtual(digitado, ctx.valores.atuais?.objeto_social);
  const codigosAtuais = chaveDosCnaes(ctx.valores.atuais?.cnaes ?? []);
  const codigosNovos = chaveDosCnaes(cnaesEstruturados.map(c => c.codigo));
  const mesmosCnaes =
    codigosNovos === codigosAtuais && (codigosAtuais !== '' || cnaesEstruturados.length === 0);
  // Sem texto e sem CNAE dos dois lados cai na guarda de baixo, com o motivo certo.
  const nadaMuda = mesmosCnaes && (textoEhOAtual || (!digitado && codigosAtuais !== ''));
  if (nadaMuda) {
    ctx.tiposNaoCobertos.push('objeto_social');
    ctx.motivos.push(
      "tipo_alteracao='objeto_social': o objeto e os CNAEs são iguais aos atuais. Altere o texto ou os CNAEs, ou desmarque o tipo.",
    );
    return;
  }
  if (textoEhOAtual) {
    ctx.avisos.push(
      'Objeto social: o texto é o do contrato atual e vai assim. Confira se ele descreve as atividades dos CNAEs marcados.',
    );
  }
  const objetoTexto =
    digitado ||
    cnaesEstruturados
      .map(c => c.descricao)
      .filter(Boolean)
      .join('; ');
  // ⚠️ SEM esta guarda o handler emitia `objeto_social_texto: ''` com
  // `cnaes: []` — e `substituir_clausula` REESCREVE a cláusula inteira. O
  // resultado não era "não mexer": era **apagar o objeto social do contrato**,
  // deixando "A sociedade tem por objeto social ." Marcar o tipo e não
  // preencher é o caminho mais provável do operador distraído.
  if (cnaesEstruturados.length === 0 && objetoTexto.length === 0) {
    ctx.tiposNaoCobertos.push('objeto_social');
    ctx.motivos.push(
      "tipo_alteracao='objeto_social': sem CNAEs e sem texto. Preencha o novo objeto — vazio apagaria a cláusula.",
    );
    return;
  }
  const patch: IPatchSubstituirClausula = {
    tipo: 'substituir_clausula',
    clausula_numero: cl.numero,
    novo_corpo: {
      objeto_social_texto: objetoTexto,
      cnaes: cnaesEstruturados,
    },
  };
  ctx.patches.push(patch);
}

/** Dados facultativos da filial (DREI, item 4.12.2). */
interface IDadosDaFilialForm {
  atividades?: string | null;
  capital_destacado?: number | string | null;
  nome_fantasia?: string | null;
}

/** Só o que foi preenchido: é o que o back escreve na cláusula e na consolidação. */
const dadosDaFilial = (
  f: IDadosDaFilialForm | null | undefined,
): { atividades?: string; capital_destacado?: number; nome_fantasia?: string } => {
  const atividades = (f?.atividades ?? '').toString().trim();
  const fantasia = (f?.nome_fantasia ?? '').toString().trim();
  const capital = Number(f?.capital_destacado);
  return {
    ...(atividades ? { atividades } : {}),
    ...(capital > 0 ? { capital_destacado: capital } : {}),
    ...(fantasia ? { nome_fantasia: fantasia } : {}),
  };
};

// Filial: o back escreve a cláusula do ato e acrescenta o parágrafo na cláusula da sede.
function tratarAberturaFilial(ctx: IBuilderCtx): void {
  const cl = localizarClausulaDeSede(ctx.vigente);
  const filiais = (ctx.valores.filiais ?? [])
    .map(f => ({
      logradouro: (f?.logradouro ?? '').toString().trim(),
      numero: (f?.numero ?? '').toString().trim(),
      complemento: (f?.complemento ?? '').toString().trim(),
      bairro: (f?.bairro ?? '').toString().trim(),
      cidade: (f?.cidade ?? '').toString().trim(),
      uf: (f?.uf ?? '').toString().trim(),
      cep: (f?.cep ?? '').toString().trim(),
      ...dadosDaFilial(f),
    }))
    // Linha toda em branco e' sobra do formulario; parcial NAO some mais calada.
    .filter(f => Object.values(f).some(Boolean));
  // Bloqueia só o que o back exige para gerar: logradouro e cidade (validators.py). Antes
  // a filial sem eles era descartada calada. Bairro, UF e CEP faltando (zona rural) geram
  // como antes do PR #772, com aviso; numero pode faltar (quadra e lote).
  const faltando = (campos: Array<[string, string]>): string[] =>
    campos.filter(([, v]) => !v).map(([nome]) => nome);
  const faltas = filiais
    .map((f, i) => {
      const falta = faltando([
        ['logradouro', f.logradouro],
        ['cidade', f.cidade],
      ]);
      return falta.length ? `filial ${i + 1} sem ${falta.join(', ')}` : '';
    })
    .filter(Boolean);
  if (cl && cl.numero != null && faltas.length > 0) {
    ctx.tiposNaoCobertos.push('abertura_filial');
    ctx.motivos.push(`tipo_alteracao='abertura_filial': ${faltas.join('; ')}.`);
    return;
  }
  const incompletas = filiais
    .map((f, i) => {
      const falta = faltando([
        ['bairro', f.bairro],
        ['UF', f.uf],
        ['CEP', f.cep],
      ]);
      return falta.length ? `Filial ${i + 1} sem ${falta.join(', ')}: sai sem isso.` : '';
    })
    .filter(Boolean);
  if (!cl || cl.numero == null || filiais.length === 0) {
    ctx.tiposNaoCobertos.push('abertura_filial');
    ctx.motivos.push(
      !cl || cl.numero == null
        ? "tipo_alteracao='abertura_filial': cláusula da sede não encontrada no vigente."
        : "tipo_alteracao='abertura_filial': informe ao menos uma filial com logradouro e cidade.",
    );
    return;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: cl.numero,
    campo: 'filial',
    valor_novo: filiais,
  };
  ctx.patches.push(patch);
  ctx.avisos.push(...incompletas);
}

// Extinção de filial: o back escreve a cláusula do ato com endereço, NIRE e CNPJ de cada uma.
// Bloqueia o que o back recusaria (validators.py): filial sem endereço, NIRE ou CNPJ.
function tratarExtincaoFilial(ctx: IBuilderCtx): void {
  // O back não usa a cláusula neste campo (a consolidação não muda); o número só cumpre o
  // schema do patch. Sem cláusula de sede no vigente o ato gera do mesmo jeito.
  const clausulaNumero = localizarClausulaDeSede(ctx.vigente)?.numero ?? 1;
  const texto = (v: unknown): string => (v ?? '').toString().trim();
  const filiais = (ctx.valores.filiais_fechar ?? [])
    .map(f => ({
      endereco: {
        logradouro: texto(f?.logradouro),
        numero: texto(f?.numero),
        complemento: texto(f?.complemento),
        bairro: texto(f?.bairro),
        cidade: texto(f?.cidade),
        uf: texto(f?.uf),
        cep: texto(f?.cep),
      },
      // Só os dígitos: o NIRE colado da certidão vem com ponto e traço.
      nire: texto(f?.nire).replace(/\D/g, ''),
      cnpj: texto(f?.cnpj),
    }))
    // Linha toda em branco é sobra do formulário.
    .filter(f => f.nire || f.cnpj || Object.values(f.endereco).some(Boolean));
  const faltas = filiais
    .map((f, i) => {
      const falta = [
        ['logradouro', f.endereco.logradouro],
        ['cidade', f.endereco.cidade],
        ['NIRE', f.nire],
        ['CNPJ', f.cnpj],
      ]
        .filter(([, v]) => !v)
        .map(([nome]) => nome);
      return falta.length ? `filial ${i + 1} sem ${falta.join(', ')}` : '';
    })
    .filter(Boolean);
  if (filiais.length === 0 || faltas.length > 0) {
    ctx.tiposNaoCobertos.push('extincao_filial');
    const motivo = faltas.join('; ') || 'informe a filial que será encerrada';
    ctx.motivos.push(`tipo_alteracao='extincao_filial': ${motivo}.`);
    return;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: clausulaNumero,
    campo: 'filial_fechar',
    valor_novo: filiais,
  };
  ctx.patches.push(patch);
  ctx.avisos.push(
    'Extinção de filial: ao gerar, o sistema retira da consolidação o que só trata da filial encerrada — a vistoria lista o que saiu ou avisa se ficou para retirar à mão.',
  );
}

// Alteração de filial: o back escreve a cláusula do ato com o CNPJ (e o NIRE, se vier), o
// endereço de hoje e o que muda — endereço novo, atividades, capital destacado ou nome fantasia.
// Bloqueia o que o back recusaria (validators.py).
function tratarAlteracaoFilial(ctx: IBuilderCtx): void {
  // Como na extinção: o back não usa a cláusula neste campo; o número só cumpre o schema.
  const clausulaNumero = localizarClausulaDeSede(ctx.vigente)?.numero ?? 1;
  const texto = (v: unknown): string => (v ?? '').toString().trim();
  const endereco = (e: IEnderecoEstruturadoForm | null | undefined) => ({
    logradouro: texto(e?.logradouro),
    numero: texto(e?.numero),
    complemento: texto(e?.complemento),
    bairro: texto(e?.bairro),
    cidade: texto(e?.cidade),
    uf: texto(e?.uf),
    cep: texto(e?.cep),
  });
  const filiais = (ctx.valores.filiais_alterar ?? [])
    .map(f => ({
      endereco: endereco(f),
      // Só os dígitos: o NIRE colado da certidão vem com ponto e traço.
      nire: texto(f?.nire).replace(/\D/g, ''),
      cnpj: texto(f?.cnpj),
      // `null` = a filial não muda de endereço (o back aceita a chave ausente).
      endereco_novo: f?.muda_endereco === false ? null : endereco(f?.novo),
      dados: dadosDaFilial(f),
    }))
    // Linha toda em branco é sobra do formulário.
    .filter(
      f =>
        f.nire ||
        f.cnpj ||
        Object.values(f.endereco).some(Boolean) ||
        Object.values(f.endereco_novo ?? {}).some(Boolean) ||
        Object.keys(f.dados).length > 0,
    );
  const faltas = filiais
    .map((f, i) => {
      const novo = f.endereco_novo;
      const itens: Array<[string, unknown]> = [
        ['CNPJ', f.cnpj],
        ['endereço de hoje (logradouro e cidade)', f.endereco.logradouro && f.endereco.cidade],
        [
          'endereço novo completo',
          !novo || (novo.logradouro && novo.bairro && novo.cidade && novo.uf && novo.cep),
        ],
        [
          'nenhuma mudança (endereço, atividades, capital destacado ou nome fantasia)',
          novo || Object.keys(f.dados).length > 0,
        ],
      ];
      const falta = itens.filter(([, v]) => !v).map(([nome]) => nome);
      return falta.length ? `filial ${i + 1} sem ${falta.join(', ')}` : '';
    })
    .filter(Boolean);
  if (filiais.length === 0 || faltas.length > 0) {
    ctx.tiposNaoCobertos.push('alteracao_filial');
    const motivo = faltas.join('; ') || 'informe a filial que será alterada';
    ctx.motivos.push(`tipo_alteracao='alteracao_filial': ${motivo}.`);
    return;
  }
  const patch: IPatchSubstituirCampo = {
    tipo: 'substituir_campo',
    clausula_numero: clausulaNumero,
    campo: 'filial_alterar',
    valor_novo: filiais.map(({ endereco_novo: novo, dados, ...f }) => ({
      ...f,
      ...(novo ? { endereco_novo: novo } : {}),
      ...dados,
    })),
  };
  ctx.patches.push(patch);
  if (filiais.some(f => f.endereco_novo)) {
    ctx.avisos.push(
      'Mudança de endereço de filial: ao gerar, o sistema troca o endereço da filial na consolidação — a vistoria diz onde trocou ou avisa se ficou para trocar à mão.',
    );
  }
  if (filiais.some(f => Object.keys(f.dados).length > 0)) {
    ctx.avisos.push(
      'Alteração de filial: ao gerar, o sistema ajusta na consolidação as atividades, o capital destacado e o nome fantasia da filial — a vistoria diz o que fez ou avisa se ficou para ajustar à mão.',
    );
  }
}

/** Dispatch table — uma função por tipo_alteracao coberto em D+E. */
// ⚠️ `Partial<Record<...>>` — o tsc NAO obriga a cobrir tipo novo aqui. Tipo
// sem handler cai em `tiposNaoCobertos` silenciosamente (o que e' o
// comportamento certo pros nao-cobertos, e uma armadilha pros que deveriam
// ter entrado). Ao adicionar um `TipoAlteracao`, confira esta tabela.
const HANDLERS: Partial<Record<TipoAlteracao, (ctx: IBuilderCtx) => void>> = {
  razao_social: tratarRazaoSocial,
  nome_fantasia: tratarNomeFantasia,
  endereco: tratarEndereco,
  abertura_filial: tratarAberturaFilial,
  extincao_filial: tratarExtincaoFilial,
  alteracao_filial: tratarAlteracaoFilial,
  capital: tratarCapital,
  objeto_social: tratarObjetoSocial,
};

/**
 * Constrói lista de patches a partir dos `tipos_alteracao` marcados +
 * valores do form + vigente parseado.
 *
 * Ordem dos patches espelha o que o operador marcou — o backend
 * aplica em sequência (ordem não importa pra `substituir_campo`
 * porque cada um afeta cláusula diferente).
 */
export function derivarPatchesV3(
  tiposAlteracao: TipoAlteracao[],
  valores: IValoresPatchesV3,
  vigente: IVigenteParserJson,
): IDerivarPatchesResult {
  const ctx: IBuilderCtx = {
    patches: [],
    tiposNaoCobertos: [],
    motivos: [],
    avisos: [],
    valores,
    vigente,
  };

  tiposAlteracao.forEach(tipo => {
    if (TIPOS_FORA_DE_DE.has(tipo)) {
      ctx.tiposNaoCobertos.push(tipo);
      ctx.motivos.push(`tipo_alteracao='${tipo}' não é coberto em D+E (Onda F).`);
      return;
    }
    // Outras cláusulas (15/10): o texto aprovado no chat vai pelo back, sem patch.
    if (
      tipo === 'quadro_societario' ||
      tipo === 'administracao' ||
      tipo === 'dados_socio' ||
      tipo === 'clausulas'
    ) {
      // Diff de sócios é derivado pelo backend a partir do payload
      // `socios[]` vs `contrato_vigente_parser_json.partes[*].socios`.
      // Sem patch explícito — coberto por convenção. Wave 2 (2026-06-19):
      // 'administracao' segue o mesmo caminho (mudança de admin via socios[],
      // sem patch) — o core consolida o quadro e reescreve a cl. de administração.
      return;
    }
    const handler = HANDLERS[tipo];
    if (handler) {
      handler(ctx);
      return;
    }
    // Sentinela: qualquer tipo_alteracao novo cai aqui até ser mapeado.
    ctx.tiposNaoCobertos.push(tipo);
    ctx.motivos.push(`tipo_alteracao='${tipo}' não tem mapeamento em derivarPatchesV3.`);
  });

  // Rascunho LEGADO: antes de 2026-08-13 a fantasia vivia dentro de "Razão
  // social" — rascunhos salvos nessa era têm `razao_social` marcado e o campo
  // `nome_fantasia` preenchido, SEM o tipo `nome_fantasia`. Sem este resgate o
  // patch de fantasia simplesmente não nascia e o valor digitado sumia do
  // documento, calado.
  // ⚠️ Só resgata se a fantasia MUDOU: o campo vem pré-preenchido com a do
  // vigente, e resgatar o mesmo nome punha "DA ADOÇÃO DE NOME FANTASIA" em todo
  // ato de razão social (contrato 137, 23/09).
  if (
    tiposAlteracao.includes('razao_social') &&
    !tiposAlteracao.includes('nome_fantasia') &&
    (valores.nome_fantasia ?? '').trim().length > 0 &&
    !fantasiaJaEstaNoVigente(vigente, valores.nome_fantasia ?? '')
  ) {
    tratarNomeFantasia(ctx);
  }

  return {
    patches: ctx.patches,
    cobertoEmDE: ctx.tiposNaoCobertos.length === 0,
    tiposNaoCobertos: ctx.tiposNaoCobertos,
    motivos: ctx.motivos,
    avisos: ctx.avisos,
  };
}
